/**
 * AddUserDialog — create a platform user (email, name, roles). Extracted
 * from `routes/admin/users/index.tsx` (issue #1411).
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { UserRole } from '../../types/users';
import { RoleCheckboxList } from './RoleCheckboxList';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';

export interface AddUserDialogProps {
  open: boolean;
  isSubmitting: boolean;
  onSubmit: (data: { email: string; name: string; roles: UserRole[] }) => void;
  onCancel: () => void;
}

export function AddUserDialog({
  open,
  isSubmitting,
  onSubmit,
  onCancel,
}: AddUserDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const titleId = useId();
  const roleIdPrefix = useId();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<UserRole[]>(['Read-Only']);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.open = open;
  }, [open]);

  const reset = (): void => {
    setEmail('');
    setName('');
    setRoles(['Read-Only']);
    setErrors({});
  };

  const handleCancel = (): void => {
    reset();
    onCancel();
  };

  const handleSubmit = (): void => {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs['email'] = 'Email is required';
    if (!name.trim()) errs['name'] = 'Name is required';
    if (roles.length === 0) errs['roles'] = 'At least one role is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSubmit({ email: email.trim(), name: name.trim(), roles });
    reset();
  };

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  return (
    <DialogElement
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby={titleId}
      className="users-dialog"
      data-testid="add-user-dialog"
    >
      <div className="users-dialog-content">
        <div className="users-dialog-header">
          <h2 id={titleId} className="forge-typography--headline5">
            Add User
          </h2>
        </div>
        <div className="users-dialog-body">
          <div className="users-form-field">
            <label htmlFor="user-email" className="forge-typography--label1">
              Email <span aria-hidden="true">*</span>
            </label>
            <input
              id="user-email"
              type="email"
              className="users-text-input"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((p) => ({ ...p, email: '' }));
              }}
              placeholder="user@example.com"
              aria-required="true"
              aria-invalid={!!errors['email']}
              data-testid="user-email-input"
            />
            {errors['email'] && (
              <span className="users-field-error" role="alert">
                {errors['email']}
              </span>
            )}
          </div>

          <div className="users-form-field">
            <label htmlFor="user-name" className="forge-typography--label1">
              Display name <span aria-hidden="true">*</span>
            </label>
            <input
              id="user-name"
              type="text"
              className="users-text-input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((p) => ({ ...p, name: '' }));
              }}
              placeholder="Jane Smith"
              aria-required="true"
              aria-invalid={!!errors['name']}
              data-testid="user-name-input"
            />
            {errors['name'] && (
              <span className="users-field-error" role="alert">
                {errors['name']}
              </span>
            )}
          </div>

          <div className="users-form-field">
            <span className="forge-typography--label1">Roles</span>
            {errors['roles'] && (
              <span className="users-field-error" role="alert">
                {errors['roles']}
              </span>
            )}
            <RoleCheckboxList
              selected={roles}
              onChange={setRoles}
              idPrefix={`add-user-${roleIdPrefix}`}
            />
          </div>
        </div>
        <div className="users-dialog-actions">
          <ForgeButton
            type="button"
            variant="outlined"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </ForgeButton>
          <ForgeButton
            type="button"
            variant="raised"
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-testid="add-user-submit"
          >
            {isSubmitting ? 'Adding…' : 'Add User'}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
